import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input"]

  static values = {
    lookupEnabled: Boolean,
    lookupUrl: String,
    currentId: String
  }

  connect() {
    this.boundOnSubmit = this.onSubmit.bind(this)
    this.formElement?.addEventListener("submit", this.boundOnSubmit)
    this.mascara()
  }

  disconnect() {
    this.formElement?.removeEventListener("submit", this.boundOnSubmit)
  }

  bloquearLetras(event) {
    const teclasPermitidas = [
      "Backspace", "Delete", "Tab", "Escape", "Enter",
      "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
      "Home", "End"
    ]

    if (event.ctrlKey || event.metaKey) return
    if (teclasPermitidas.includes(event.key)) return
    if (/^\d$/.test(event.key)) return

    event.preventDefault()
  }

  colarSoDigitos(event) {
    event.preventDefault()

    const texto = (event.clipboardData || window.clipboardData).getData("text")
    const digitos = this.soDigitos(texto).slice(0, 11)

    this.inputTarget.value = this.aplicarMascara(digitos)
    this.limparErro()
    this.limparAvisoCadastro()
  }

  mascara() {
    const digitos = this.soDigitos(this.inputTarget.value).slice(0, 11)
    this.inputTarget.value = this.aplicarMascara(digitos)
    this.limparErro()
  }

  validar() {
    const cpf = this.soDigitos(this.inputTarget.value)

    if (cpf.length === 0) {
      this.limparErro()
      this.limparAvisoCadastro()
      return
    }

    if (cpf.length < 11) {
      this.marcarErro("CPF incompleto")
      this.limparAvisoCadastro()
      return
    }

    if (!this.cpfValido(cpf)) {
      this.marcarErro("CPF inválido")
      this.limparAvisoCadastro()
      return
    }

    this.limparErro()
  }

  async buscarCadastro() {
    if (!this.lookupEnabledValue) return
    if (!this.hasLookupUrlValue || !this.lookupUrlValue) return

    const cpf = this.soDigitos(this.inputTarget.value)

    if (cpf.length !== 11 || !this.cpfValido(cpf)) {
      this.limparAvisoCadastro()
      return
    }

    try {
      const url = new URL(this.lookupUrlValue, window.location.origin)
      url.searchParams.set("cpf", cpf)

      const response = await fetch(url.toString(), {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        this.limparAvisoCadastro()
        return
      }

      const data = await response.json()

      if (data.found && String(data.id) !== String(this.currentIdValue || "")) {
        this.mostrarAvisoCadastro(data)
      } else {
        this.limparAvisoCadastro()
      }
    } catch (_error) {
      this.limparAvisoCadastro()
    }
  }

  onSubmit() {
    this.inputTarget.value = this.soDigitos(this.inputTarget.value)
  }

  soDigitos(valor) {
    return (valor || "").replace(/\D/g, "")
  }

  aplicarMascara(valor) {
    const cpf = this.soDigitos(valor).slice(0, 11)
    let resultado = ""

    for (let i = 0; i < cpf.length; i++) {
      if (i === 3 || i === 6) resultado += "."
      if (i === 9) resultado += "-"
      resultado += cpf[i]
    }

    return resultado
  }

  cpfValido(cpf) {
    if (!cpf || cpf.length !== 11) return false
    if (/^(\d)\1{10}$/.test(cpf)) return false

    let soma = 0
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i), 10) * (10 - i)
    }

    let digito1 = 11 - (soma % 11)
    digito1 = digito1 >= 10 ? 0 : digito1
    if (digito1 !== parseInt(cpf.charAt(9), 10)) return false

    soma = 0
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i), 10) * (11 - i)
    }

    let digito2 = 11 - (soma % 11)
    digito2 = digito2 >= 10 ? 0 : digito2
    if (digito2 !== parseInt(cpf.charAt(10), 10)) return false

    return true
  }

  marcarErro(mensagem) {
    this.inputTarget.classList.add("is-invalid")
    this.feedbackElement().textContent = mensagem
  }

  limparErro() {
    this.inputTarget.classList.remove("is-invalid")
    this.feedbackElement().textContent = ""
  }

  feedbackElement() {
    let next = this.inputTarget.nextElementSibling

    if (next && next.classList.contains("invalid-feedback")) {
      return next
    }

    const div = document.createElement("div")
    div.className = "invalid-feedback"
    this.inputTarget.insertAdjacentElement("afterend", div)
    return div
  }

  mostrarAvisoCadastro(data) {
    const box = this.lookupElement()
    const nome = data.nome || "Pessoa já cadastrada"
    const url = data.redirect_url

    box.innerHTML = url
      ? `Já existe cadastro para <strong>${this.escapeHtml(nome)}</strong>. <a href="${this.escapeAttr(url)}">Abrir cadastro</a>`
      : `Já existe cadastro para <strong>${this.escapeHtml(nome)}</strong>.`

    box.classList.remove("d-none")
  }

  limparAvisoCadastro() {
    const box = this.lookupElement()
    box.innerHTML = ""
    box.classList.add("d-none")
  }

  lookupElement() {
    let el = this.element.parentElement.querySelector(".cpf-lookup-feedback")
    if (el) return el

    el = document.createElement("div")
    el.className = "cpf-lookup-feedback alert alert-warning mt-2 mb-0 py-2 d-none"
    this.inputTarget.insertAdjacentElement("afterend", el)
    return el
  }

  escapeHtml(value) {
    const div = document.createElement("div")
    div.textContent = value || ""
    return div.innerHTML
  }

  escapeAttr(value) {
    return String(value || "").replace(/"/g, "&quot;")
  }

  get formElement() {
    return this.element.form || this.element.closest("form")
  }
}
